import { useState, useMemo, useEffect } from "react";
import {
  Box,
  Button,
  Typography,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Grid,
  MenuItem,
  Autocomplete,
  useTheme
} from "@mui/material";
import CustomAutocomplete from "components/mui/autocomplete";
import CustomTextField from "components/mui/text-field";
import { GetEmployees } from "../logic/PunchClockLogic";
import { saveJob } from "screens/WorkOrders/logic/WorkRequirementScheduleLogic";
import { toast } from "react-toastify";
import { useNavigate } from "react-router-dom";
import { SaveServiceRequest } from "screens/DispatchBoard/logic/DispatchBoardLogic";
import Chip from "@mui/material/Chip";

const TASK_TYPES = [
  "Request",
  "Assessment",
  "Estimate",
  "Work Order",
  "Work Order Job",
  "Assessment Job",
];

const AssignTask = ({ open, handleClose, tasks, fetchAndJoin }) => {
  // -- State for dropdowns and auto-filling
  const theme = useTheme();
  const [selectedType, setSelectedType] = useState("");
  const [selectedId, setSelectedId] = useState("");
  const [manualTitle, setManualTitle] = useState("");
  const [manualAssignees, setManualAssignees] = useState([]);
  const [manualDescription, setManualDescription] = useState("");
  const [employees, setEmployees] = useState([]);
  const [errors, setErrors] = useState({
    manualTitle: "",
    // …other fields
  });

  const navigate = useNavigate();
  // Filter IDs for selected Type
  const availableIds = useMemo(
    () =>
      tasks
        ? tasks.filter((task) => task.Type === selectedType)
        : [],
    [tasks, selectedType]
  );

  // Find the selected task (by id)
  const selectedTask = useMemo(
    () =>
      availableIds.find((task) => task.ID === selectedId) || null,
    [availableIds, selectedId]
  );

  // When type or id changes, update form fields from selected task
  useEffect(() => {
    if (selectedTask) {
      setManualTitle(selectedTask.title || "");
      // Extract all assigned employee IDs from the task
      const assignedIds = Array.isArray(selectedTask.assignedTo)
        ? selectedTask.assignedTo.map(
            user => user.employeeID || user.userID // covers both cases
          )
        : [];
      // Filter employee objects for those IDs
      setManualAssignees(
        employees.filter(emp => assignedIds.includes(emp.employeeID))
      );
      setManualDescription(selectedTask.description || "");

    } else {
      setManualTitle("");
      setManualAssignees([]);
      setManualDescription("");
    }
  }, [selectedTask, employees]);  

  useEffect(() => {
    if (Object.keys(errors).length > 0) {
      const timer = setTimeout(() => {
        setErrors({});
      }, 5000);
  
      return () => clearTimeout(timer);
    }
  }, [errors]);

  useEffect(() => {
    (async () => {
      try {
        const resp = await GetEmployees(0);         // wrapper must return the full data obj
        setEmployees(resp?.result || []);            // grab the array
      } catch (err) {
        console.error("Error loading employees", err);
      }
    })();
  }, []) 

  const handleAssigneesChange = (event, newValue) => {    
    
    setManualAssignees(newValue);
  };
  
  const getTitleLabel = (type) => {
  switch (type) {
    case "Request":
      return "Request Title";
    case "Assessment":
      return "Assessment Title";
    case "Estimate":
      return "Estimate Title";
    case "Work Order":
      return "Work order Title";
    case "Work Order Job":
      return "Work order Title";
    case "Assessment Job":
      return "Assessment Title";
    default:
      return "Title";
  }
};

const getDescriptionLabel = (type) => {
  switch (type) {
    case "Request":
      return "Request Description";
    case "Assessment":
      return "Assessment Description";
    case "Estimate":
      return "Estimate Description";
    case "Work Order":
      return "Work order Description";
    case "Work Order Job":
      return "Work order Description";
    case "Assessment Job":
      return "Assessment Description";
    default:
      return "Description";
  }
};

const isJobType = (type) => type === "Work Order Job" || type === "Assessment Job";
const isFormFieldsEnabled = selectedType && selectedId;

function getEmployeeIDs(arr) {
  // Returns array of employee IDs, or null if empty/invalid
  if (!Array.isArray(arr) || arr.length === 0) return [];
  return arr.map(a => a.employeeID);
}
function getAssignedToUsers(arr) {
  // Returns array of { userID } or null if empty/invalid
  if (!Array.isArray(arr) || arr.length === 0) return [];
  return arr.map(a => ({ employeeID: a.employeeID }));
}

const handleSubmit = async () => {
  // 1. Find the original task object (ensure you have all original fields)
  const validationErrors = {};
  if (!manualTitle.trim()) {
    validationErrors.manualTitle = `${getTitleLabel(selectedType)} is required.`;
  }
  // (you could add more field checks here)
  if (Object.keys(validationErrors).length) {
    setErrors(prev => ({ ...prev, ...validationErrors }));
    return;   // stop submission
  }
  
  const origTask = selectedTask;

  // 2. Compute employee IDs array (null if empty)
  const employeeIDs = getEmployeeIDs(manualAssignees);

  // 3. Build payload based on type
  if (selectedType === "Assessment Job" || selectedType === "Work Order Job") {
    // --- JOB PAYLOAD ---
    // Find all job-level data (in your rowsAssignedTasks or original joinedData if needed)
    // We'll assume origTask has all required job fields

    const jobPayload = {
      jobID: origTask.jobID,
      workReqID: origTask.workReqID,
      assignees: employeeIDs, // null if empty
    };

    try {
      const result = await saveJob(jobPayload, navigate);
      if (result && !result.isError) {
        toast.success("Task Updated Successfully!", {
      style: {
        fontFamily: '"DM Sans", sans-serif',
        fontSize: '15px',
      },
    });
        fetchAndJoin();
        handleResetAndClose();
      } else {
        handleClose();
        toast.error(result?.message || "Failed to update Task.", {
      style: {
        fontFamily: '"DM Sans", sans-serif',
        fontSize: '15px',
      },
    });
      }
    } catch (e) {
      // handle error
      toast.error("Failed to update Task.", {
      style: {
        fontFamily: '"DM Sans", sans-serif',
        fontSize: '15px',
      },
    });
    }
  } else {
    // --- SERVICE REQUEST PAYLOAD ---
    // Use edited fields where applicable, fallback to original for the rest

    const srPayload = {
      requestID: origTask.requestID,
      requestTitle: manualTitle || origTask.requestTitle,
      reqDescription: manualDescription || origTask.reqDescription,
      requestSource: origTask.requestSource || "Internal",
      status: origTask.status || "Submitted",
      availabilityTime: origTask.availabilityTime || [],
      availabilityDays: origTask.availabilityDays || [],
      tags: origTask.tags || [],
      assignedToUsers: getAssignedToUsers(manualAssignees), // null if empty
      entityType: selectedType,
    };

    try {
      const result = await SaveServiceRequest(srPayload, navigate, selectedType);
      if (result && !result.isError) {
        toast.success("Task Updated Successfully!", {
      style: {
        fontFamily: '"DM Sans", sans-serif',
        fontSize: '15px',
      },
    });
        fetchAndJoin();
        handleResetAndClose();
      } else {
        handleClose();
        toast.error(result?.message || "Failed to update Task.", {
      style: {
        fontFamily: '"DM Sans", sans-serif',
        fontSize: '15px',
      },
    });
      }
    } catch (e) {
      // handle error
      handleClose();
      toast.error("Failed to update Task.", {
      style: {
        fontFamily: '"DM Sans", sans-serif',
        fontSize: '15px',
      },
    });
    }
  }
};

const handleResetAndClose = () => {
  setSelectedType("");
  setSelectedId("");
  setManualTitle("");
  setManualAssignees([]);
  setManualDescription("");
  handleClose();
}

  return (
    <Dialog
      fullWidth
      maxWidth="sm"
      open={open}
      onClose={handleResetAndClose}
      aria-labelledby="form-dialog-title"
    >
      <DialogTitle
        sx={{
          borderBottom: "1px solid #D9D9D9",
          paddingBottom: "20px !important",
        }}
      >
        <Box>
          <Typography variant="h4" fontWeight="bold">
            Assign Task
          </Typography>
        </Box>
      </DialogTitle>
      <DialogContent>
        <Grid container spacing={5}>
         <Grid item xs={12} md={6}>
            <Autocomplete
              options={TASK_TYPES.sort()} 
              value={selectedType}
              onChange={(event, newValue) => {
                setSelectedType(newValue || "");
                setSelectedId(""); 
              }}
              renderInput={(params) => (
                <CustomTextField
                  {...params}
                  fullWidth
                  label="Task Type"
                  required
                  placeholder="Select Task Type"
                  InputLabelProps={{ shrink: !!selectedType }}
                />
              )}
              renderOption={(props, option) => (
                <MenuItem {...props}>
                  {option}
                </MenuItem>
              )}
            />
          </Grid>
         <Grid item xs={12} md={6}>
              <CustomAutocomplete
                options={availableIds.sort((a, b) => a.ID.localeCompare(b.ID))} 
                getOptionLabel={(option) => option?.ID || ""}
                value={
                  availableIds.find((item) => item.ID === selectedId) || null
                }
                onChange={(e, newValue) =>
                  setSelectedId(newValue ? newValue.ID : "")
                }
                disabled={!selectedType}
                sx={{ minWidth: 280 }}
                renderInput={(params) => (
                  <CustomTextField
                    {...params}
                    label="ID"
                    placeholder="Select ID"
                    required
                    InputLabelProps={{ shrink: true }}
                  />
                )}
              />
            </Grid>
          <Grid item xs={12} md={6}>
            <CustomTextField
              fullWidth
              label={getTitleLabel(selectedType)}
              required
              value={manualTitle}
              placeholder={`Enter ${getTitleLabel(selectedType)}`}
              onBlur={(e) => {
                if (!e.target.value.trim()) {
                  setErrors((prev) => ({
                    ...prev,
                    manualTitle: `${getTitleLabel(selectedType)} is required.`,
                  }));
                }
              }}
              onChange={(e) => {
                setManualTitle(e.target.value);
                if (errors.manualTitle) {
                  setErrors((prev) => ({ ...prev, manualTitle: "" }));
                }
              }}
              error={!!errors.manualTitle}
              helperText={errors.manualTitle}
              disabled={!isFormFieldsEnabled || isJobType(selectedType)}
              InputProps={{
                readOnly: !isFormFieldsEnabled || isJobType(selectedType),
              }}
            />
          </Grid>
                 <Grid item xs={12} md={6}>
                <CustomAutocomplete
                  multiple
                  options={employees.sort((a, b) => 
                    `${a.firstName} ${a.lastName}`.localeCompare(`${b.firstName} ${b.lastName}`)
                  )} 
                  getOptionLabel={(opt) => `${opt.firstName} ${opt.lastName}`}
                  value={manualAssignees}
                  onChange={handleAssigneesChange}
                  noOptionsText="No employees"
                  disabled={!isFormFieldsEnabled}
                  sx={{ mb: 4, width: 280 }}
                  PopperProps={{
                    modifiers: [
                      { name: "flip", enabled: false },
                      { name: "preventOverflow", enabled: false },
                    ],
                  }}
                  renderTags={(value, getTagProps) => {
                    const chipsToShow = value.slice(0, 1);
                    const remainingCount = value.length - 1;

                    return [
                      ...chipsToShow.map((option, index) => (
                        <Chip
                          {...getTagProps({ index })}
                          key={option.employeeID}
                          size="small"
                          sx={{
                            width: 110,
                            height: 25,
                            fontSize: 13,
                            overflow: "hidden",
                          }}
                          label={
                            <Box
                              component="span"
                              sx={{
                                width: "100%",
                                overflow: "hidden",
                                whiteSpace: "nowrap",
                                textOverflow: "ellipsis",
                              }}
                            >
                              {`${option.firstName} ${option.lastName}`}
                            </Box>
                          }
                        />
                      )),
                      ...(remainingCount > 0
                        ? [
                            <Chip
                              key="more"
                              label={`+${remainingCount}`}
                              size="small"
                              sx={{
                                width: 45,
                                height: 30,
                                fontSize: 13,
                              }}
                            />,
                          ]
                        : []),
                    ];
                  }}
                  renderInput={(params) => (
                    <CustomTextField
                      {...params}
                      label="Assign To"
                      placeholder="Select assignees"
                      fullWidth
                    />
                  )}
                />
              </Grid>
          <Grid item xs={12} md={6}>
            <CustomTextField
              fullWidth
              label={getDescriptionLabel(selectedType)}
              value={manualDescription}
              placeholder={`Enter ${getDescriptionLabel(selectedType)}`}
              onChange={(e) => setManualDescription(e.target.value)}
              disabled={!isFormFieldsEnabled || isJobType(selectedType)}
              InputProps={{
                readOnly: !isFormFieldsEnabled || isJobType(selectedType),
              }}
            />
          </Grid>
        </Grid>
      </DialogContent>
      <DialogActions
        sx={{
          display: "flex",
          justifyContent: "space-between",
          padding: "20px 24px !important",
          boxShadow: `0px -4px 8px rgb(0, 0, 0, 0.1)`,
          zIndex: 1,
        }}
        className="dialog-actions-dense"
      >
        <Box>
          <Button
            variant="contained"
            color="info"
            onClick={handleResetAndClose}
          >
            Cancel
          </Button>
        </Box>
        <Box sx={{ display: "flex", gap: 2 }}>
          <Button
            variant="text"
            color={theme.palette.mode === 'dark' ? "secondary" : "primary"}
            onClick={() => {
              setSelectedType("");
              setSelectedId("");
              setManualTitle("");
              setManualAssignees([]);
              setManualDescription("");
            }}
          >
            Reset
          </Button>
          <Button
            variant="contained"
            color="main"
            onClick={handleSubmit}
            // disabled={!isFormFieldsEnabled} // Optional: prevent submit if not ready
          >
            Assign Task
          </Button>
        </Box>
      </DialogActions>
    </Dialog>
  );
};

export default AssignTask;